<template>
  <div
    class="wage-payroll-grid"
    :class="[
      gridClass,
      { 'wage-payroll-grid--with-fix-cb': alignWithFixCheckbox },
    ]"
  >
    <template v-for="field in fields" :key="field.key">
      <HomeWageTooltip
        v-if="showTooltips && (getTooltip ? getTooltip(field.key) : field.tooltip)"
        location="top"
      >
        <template #activator="{ props: tooltipProps }">
          <label
            v-bind="tooltipProps"
            class="wage-payroll-grid__label wage-payroll-grid__label--tooltip"
            :class="{ 'wage-payroll-grid__label--total': isTotal(field) }"
            :for="field.premiumHours && field.hoursKey ? `payroll-hours-${field.hoursKey}` : `payroll-${field.key}`"
          >{{ field.label }}</label>
        </template>
        <span style="white-space: pre-line">{{ getTooltip ? getTooltip(field.key) : field.tooltip }}</span>
      </HomeWageTooltip>
      <label
        v-else
        class="wage-payroll-grid__label"
        :class="{ 'wage-payroll-grid__label--total': isTotal(field) }"
        :for="field.premiumHours && field.hoursKey ? `payroll-hours-${field.hoursKey}` : `payroll-${field.key}`"
      >{{ field.label }}</label>
      <span
        v-if="alignWithFixCheckbox"
        class="wage-insurance-single__fix-cb-spacer"
        aria-hidden="true"
      />
      <span
        v-if="field.premiumHours && hidePremiumHours"
        :id="`payroll-${field.key}`"
        class="wage-payroll-grid__value wage-payroll-grid__value--premium-amount"
        :title="`${field.label} (생산직 비과세 — 금액만 표시)`"
      >{{ formatMoney(field.key) }}</span>
      <div
        v-else-if="field.premiumHours && field.hoursKey"
        class="wage-payroll-premium"
      >
        <input
          :id="`payroll-hours-${field.hoursKey}`"
          type="text"
          class="wage-payroll-premium__hours"
          :class="{ 'wage-payroll-premium__hours--readonly': premiumHoursReadonly }"
          :value="formatPremiumHours(field.hoursKey)"
          placeholder="시간"
          inputmode="decimal"
          :readonly="premiumHoursReadonly"
          :tabindex="premiumHoursReadonly ? -1 : undefined"
          @input="emit('premium-hours-input', field.hoursKey, $event)"
          @blur="emit('premium-hours-blur', field.hoursKey, field.key)"
          @keydown.enter="($event.target as HTMLInputElement).blur()"
        />
        <input
          :id="`payroll-${field.key}`"
          type="text"
          class="wage-payroll-premium__amount wage-payroll-grid__input wage-payroll-grid__input--readonly"
          :value="formatMoney(field.key)"
          readonly
          tabindex="-1"
          :title="`${field.label} (시간×시급 자동계산)`"
        />
      </div>
      <input
        v-else-if="field.money && field.readonly"
        :id="`payroll-${field.key}`"
        type="text"
        class="wage-payroll-grid__input wage-payroll-grid__input--readonly"
        :value="formatMoney(field.key)"
        readonly
        tabindex="-1"
      />
      <input
        v-else-if="field.money"
        :id="`payroll-${field.key}`"
        type="text"
        class="wage-payroll-grid__input"
        :class="{
          'wage-payroll-grid__input--linked': field.hourlyLinked,
          'wage-payroll-grid__input--total': isTotal(field),
        }"
        :value="formatMoney(field.key)"
        inputmode="numeric"
        @input="emit('money-input', field.key, $event)"
        @blur="emit('money-blur', field.key)"
        @keydown.enter="($event.target as HTMLInputElement).blur()"
      />
      <input
        v-else
        :id="`payroll-${field.key}`"
        :value="textValue(field.key)"
        type="text"
        class="wage-payroll-grid__input"
        @input="emit('text-input', field.key, $event)"
        @keydown.enter="($event.target as HTMLInputElement).blur()"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import {
  isHomeWagePayrollTotalKey,
  type HomeWagePayrollFieldDef,
  type HomeWagePayrollFieldKey,
  type HomeWagePremiumAmountKey,
  type HomeWagePremiumHoursKey,
} from '@/policies';
import HomeWageTooltip from '@/components/wage/home/HomeWageTooltip.vue';

const injectedShowTooltips = inject<{ value: boolean } | boolean>('showWageTooltips', true);
const showTooltips = computed(() => {
  if (typeof injectedShowTooltips === 'boolean') return injectedShowTooltips;
  if (injectedShowTooltips && typeof injectedShowTooltips === 'object' && 'value' in injectedShowTooltips) {
    return injectedShowTooltips.value;
  }
  return true;
});

defineProps<{
  fields: HomeWagePayrollFieldDef[];
  gridClass?: string;
  formatMoney: (key: HomeWagePayrollFieldKey) => string;
  formatPremiumHours: (key: HomeWagePremiumHoursKey) => string;
  textValue: (key: HomeWagePayrollFieldKey) => string;
  getTooltip?: (key: HomeWagePayrollFieldKey) => string;
  /** 생산직 비과세 분리 UI — 월 20만 금액 한도는 시간으로 정확히 쪼개지 않으므로 금액만 표시 */
  hidePremiumHours?: boolean;
  /** 4대보험·공제 고정 체크열과 입력란 열 정렬 (체크 없는 행은 spacer) */
  alignWithFixCheckbox?: boolean;
  /** 센터장 시점 등 — 연·야·휴 시간 입력 비활성 (표시만 빈값) */
  premiumHoursReadonly?: boolean;
}>();

const emit = defineEmits<{
  'money-input': [key: HomeWagePayrollFieldKey, event: Event];
  'money-blur': [key: HomeWagePayrollFieldKey];
  'premium-hours-input': [key: HomeWagePremiumHoursKey, event: Event];
  'premium-hours-blur': [key: HomeWagePremiumHoursKey, amountKey: HomeWagePremiumAmountKey];
  'text-input': [key: HomeWagePayrollFieldKey, event: Event];
}>();

const isTotal = (field: HomeWagePayrollFieldDef) =>
  !!field.isTotal || isHomeWagePayrollTotalKey(field.key);
</script>
